import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Calendar, Plus, X, ArrowRight, ArrowLeft, Upload, Loader2, 
  Check, Heart, Cigarette, Wine, Baby, Star, Sparkles, Camera, User,
  Shield, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile, uploadPhoto } from '../services/api';

const TOTAL_STEPS = 7;

const ProfileSetupPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    // Step 1
    display_name: user?.display_name || user?.name || '',
    date_of_birth: user?.date_of_birth || '',
    gender: user?.gender || '',
    from_country: user?.from_country || '',
    from_region: user?.from_region || '',
    from_city: user?.from_city || '',
    current_country: user?.current_country || '',
    current_region: user?.current_region || '',
    current_city: user?.current_city || '',
    height_cm: user?.height_cm || '',
    ethnicity: user?.ethnicity || '',
    languages: user?.languages || [],
    zodiac: user?.zodiac || '',
    // Step 2
    looking_for: user?.looking_for || '',
    interested_in: user?.interested_in || '',
    age_min: user?.age_min || 18,
    age_max: user?.age_max || 45,
    distance_preference: user?.distance_preference || '',
    // Step 3
    has_children: user?.has_children || '',
    wants_children: user?.wants_children || '',
    smoking: user?.smoking || '',
    drinking: user?.drinking || '',
    education: user?.education || '',
    occupation: user?.occupation || '',
    // Step 4
    interests: user?.interests || [],
    // Step 5
    personality: user?.personality || [],
    ideal_date: user?.ideal_date || '',
    relationship_priorities: user?.relationship_priorities || [],
    // Step 6
    bio: user?.bio || '',
    prompts: user?.prompts || [],
    // Step 7
    photos: user?.photos || [],
    primary_photo_index: user?.primary_photo_index ?? 0,
    photo_blurred: user?.photo_blurred ?? false,
  });

  // Auto-save on step change
  const saveProgress = async (data = formData) => {
    try {
      const payload = { ...data };
      // Convert age_min/age_max to numbers
      if (payload.age_min) payload.age_min = parseInt(payload.age_min);
      if (payload.age_max) payload.age_max = parseInt(payload.age_max);
      await updateProfile(payload);
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  const getAge = () => {
    if (!formData.date_of_birth) return null;
    const dob = new Date(formData.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  const canProceed = () => {
    const age = getAge();
    switch (step) {
      case 1:
        return formData.display_name.trim().length >= 2 &&
               formData.date_of_birth && age >= 18 &&
               formData.gender && formData.current_country.trim().length > 0;
      case 2:
        return formData.looking_for && formData.interested_in;
      case 3:
        return true; // All optional
      case 4:
        return formData.interests.length >= 1;
      case 5:
        return true;
      case 6:
        return true; // Bio and prompts optional
      case 7:
        return formData.photos.length >= 1;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (!canProceed()) return;
    setError('');
    // Auto-save current step data
    await saveProgress();
    if (step < TOTAL_STEPS) {
      setStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(s => s - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...formData,
        age_min: parseInt(formData.age_min),
        age_max: parseInt(formData.age_max),
        onboarding_completed: true,
      };
      const response = await updateProfile(payload);
      updateUser(response.data);
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item, maxCount = null) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      if (arr.includes(item)) {
        return { ...prev, [field]: arr.filter(i => i !== item) };
      }
      if (maxCount && arr.length >= maxCount) return prev;
      return { ...prev, [field]: [...arr, item] };
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setError('Please upload a valid image (JPEG, PNG, WebP, or GIF)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB');
      return;
    }
    if (formData.photos.length >= 6) {
      setError('Maximum 6 photos allowed');
      return;
    }

    setUploadingPhoto(true);
    setError('');
    try {
      const response = await uploadPhoto(file);
      const photoUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, photos: [...prev.photos, photoUrl] }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => {
      const newPhotos = prev.photos.filter((_, i) => i !== index);
      let newPrimary = prev.primary_photo_index;
      if (index === prev.primary_photo_index) newPrimary = 0;
      else if (index < prev.primary_photo_index) newPrimary = prev.primary_photo_index - 1;
      return { ...prev, photos: newPhotos, primary_photo_index: newPrimary };
    });
  };

  const stepTitles = [
    'About You',
    "What Are You Looking For?",
    'Your Lifestyle',
    'Your Interests',
    'Your Personality',
    'Your Story',
    'Add Photos'
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="profile-setup">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        data-testid="photo-file-input"
      />

      {/* Progress Header */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={handleBack}
              disabled={step === 1}
              className="p-2 -ml-2 rounded-full hover:bg-gray-100 disabled:opacity-30 transition-colors"
              data-testid="back-step-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="text-sm font-medium text-gray-500">
              Step {step} of {TOTAL_STEPS}
            </div>
            <div className="w-9" />
          </div>
          <div className="flex gap-1">
            {[...Array(TOTAL_STEPS)].map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i < step ? 'bg-[#009E60]' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-xl mx-auto px-4 py-6 pb-32">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              {stepTitles[step - 1]}
            </h1>

            {/* STEP 1: About You */}
            {step === 1 && <Step1AboutYou formData={formData} updateField={updateField} toggleArrayItem={toggleArrayItem} getAge={getAge} />}
            
            {/* STEP 2: Looking For */}
            {step === 2 && <Step2LookingFor formData={formData} updateField={updateField} />}
            
            {/* STEP 3: Lifestyle */}
            {step === 3 && <Step3Lifestyle formData={formData} updateField={updateField} />}
            
            {/* STEP 4: Interests */}
            {step === 4 && <Step4Interests formData={formData} toggleArrayItem={toggleArrayItem} />}
            
            {/* STEP 5: Personality */}
            {step === 5 && <Step5Personality formData={formData} updateField={updateField} toggleArrayItem={toggleArrayItem} />}
            
            {/* STEP 6: Story */}
            {step === 6 && <Step6Story formData={formData} updateField={updateField} />}
            
            {/* STEP 7: Photos */}
            {step === 7 && (
              <Step7Photos
                formData={formData}
                updateField={updateField}
                removePhoto={removePhoto}
                uploadingPhoto={uploadingPhoto}
                onUploadClick={() => fileInputRef.current?.click()}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {error && (
          <div className="mt-4 p-4 bg-red-50 text-red-600 rounded-xl text-sm" data-testid="setup-error">
            {error}
          </div>
        )}
      </div>

      {/* Fixed Footer with Next/Finish */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-gray-100 safe-bottom z-30">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/home')}
            className="text-gray-500 text-sm hover:text-gray-700 px-2"
            data-testid="skip-onboarding"
          >
            Skip
          </button>
          {step < TOTAL_STEPS ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex-1 h-12 bg-[#009E60] hover:bg-[#007A4B] rounded-full text-white font-semibold"
              data-testid="next-btn"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleFinish}
              disabled={loading || !canProceed()}
              className="flex-1 h-12 bg-[#009E60] hover:bg-[#007A4B] rounded-full text-white font-semibold"
              data-testid="finish-btn"
            >
              {loading ? 'Saving...' : 'Start Dating'}
              <Heart className="w-4 h-4 ml-2" fill="white" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============= STEP COMPONENTS =============

const ChoiceCard = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
      selected
        ? 'border-[#009E60] bg-[#009E60]/5 text-gray-900'
        : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
    }`}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <span className="font-medium">{children}</span>
      {selected && (
        <div className="w-6 h-6 rounded-full bg-[#009E60] flex items-center justify-center flex-shrink-0">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  </button>
);

const Chip = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
      selected
        ? 'border-[#009E60] bg-[#009E60] text-white'
        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
    }`}
    data-testid={testId}
  >
    {children}
  </button>
);

const Step1AboutYou = ({ formData, updateField, toggleArrayItem, getAge }) => {
  const age = getAge();
  const genders = [
    { value: 'man', label: 'Man' },
    { value: 'woman', label: 'Woman' },
    { value: 'non-binary', label: 'Non-binary' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
  ];

  return (
    <div className="space-y-6 mt-6">
      <p className="text-gray-600 -mt-4">Let's start with the basics</p>

      <div>
        <Label className="text-gray-900 font-semibold">What should people call you?</Label>
        <div className="mt-2 relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={formData.display_name}
            onChange={(e) => updateField('display_name', e.target.value)}
            placeholder="First name or display name"
            className="pl-10 h-12 rounded-xl"
            data-testid="display-name-input"
          />
        </div>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">When were you born?</Label>
        <div className="mt-2 relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="date"
            value={formData.date_of_birth}
            onChange={(e) => updateField('date_of_birth', e.target.value)}
            max={new Date(Date.now() - 18*365.25*24*60*60*1000).toISOString().split('T')[0]}
            className="pl-10 h-12 rounded-xl"
            data-testid="dob-input"
          />
        </div>
        {age !== null && age >= 0 && (
          <p className={`text-sm mt-1 ${age >= 18 ? 'text-gray-500' : 'text-red-600'}`}>
            {age >= 18 ? `You're ${age} years old` : 'You must be 18 or older'}
          </p>
        )}
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">What is your gender?</Label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {genders.map(g => (
            <ChoiceCard
              key={g.value}
              selected={formData.gender === g.value}
              onClick={() => updateField('gender', g.value)}
              testId={`gender-${g.value}`}
            >
              {g.label}
            </ChoiceCard>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">Where are you from?</Label>
        <div className="mt-2 space-y-2">
          <Input
            value={formData.from_country}
            onChange={(e) => updateField('from_country', e.target.value)}
            placeholder="Country (e.g., Guyana)"
            className="h-12 rounded-xl"
            data-testid="from-country-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={formData.from_region}
              onChange={(e) => updateField('from_region', e.target.value)}
              placeholder="Region/Province"
              className="h-12 rounded-xl"
              data-testid="from-region-input"
            />
            <Input
              value={formData.from_city}
              onChange={(e) => updateField('from_city', e.target.value)}
              placeholder="City"
              className="h-12 rounded-xl"
              data-testid="from-city-input"
            />
          </div>
        </div>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">Where do you currently live?</Label>
        <div className="mt-2 space-y-2">
          <Input
            value={formData.current_country}
            onChange={(e) => updateField('current_country', e.target.value)}
            placeholder="Country"
            className="h-12 rounded-xl"
            data-testid="current-country-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={formData.current_region}
              onChange={(e) => updateField('current_region', e.target.value)}
              placeholder="Region/Province"
              className="h-12 rounded-xl"
              data-testid="current-region-input"
            />
            <Input
              value={formData.current_city}
              onChange={(e) => updateField('current_city', e.target.value)}
              placeholder="City"
              className="h-12 rounded-xl"
              data-testid="current-city-input"
            />
          </div>
        </div>
      </div>

      {/* Extra optional fields */}
      <div>
        <Label className="text-gray-900 font-semibold">Height <span className="text-gray-400 font-normal text-sm">(optional)</span></Label>
        <div className="mt-2">
          <Input
            type="number"
            min="120"
            max="230"
            value={formData.height_cm}
            onChange={(e) => updateField('height_cm', e.target.value ? parseInt(e.target.value) : '')}
            placeholder="Height in cm (e.g., 170)"
            className="h-12 rounded-xl"
            data-testid="height-input"
          />
        </div>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">Ethnicity <span className="text-gray-400 font-normal text-sm">(optional)</span></Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {['Indo-Guyanese', 'Afro-Guyanese', 'Amerindian', 'Portuguese-Guyanese', 'Chinese-Guyanese', 'Mixed', 'Other', 'Prefer not to say'].map(e => (
            <Chip
              key={e}
              selected={formData.ethnicity === e}
              onClick={() => updateField('ethnicity', formData.ethnicity === e ? '' : e)}
              testId={`ethnicity-${e.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            >
              {e}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">Languages you speak <span className="text-gray-400 font-normal text-sm">(optional, up to 5)</span></Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {['English', 'Guyanese Creole', 'Hindi', 'Spanish', 'Portuguese', 'Dutch', 'French', 'Chinese', 'Arabic'].map(l => (
            <Chip
              key={l}
              selected={(formData.languages || []).includes(l)}
              onClick={() => toggleArrayItem('languages', l, 5)}
              testId={`lang-${l.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            >
              {l}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">Zodiac sign <span className="text-gray-400 font-normal text-sm">(optional)</span></Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'].map(z => (
            <Chip
              key={z}
              selected={formData.zodiac === z}
              onClick={() => updateField('zodiac', formData.zodiac === z ? '' : z)}
              testId={`zodiac-${z.toLowerCase()}`}
            >
              {z}
            </Chip>
          ))}
        </div>
      </div>
    </div>
  );
};

const Step2LookingFor = ({ formData, updateField }) => {
  const intentions = [
    { value: 'long-term', label: 'Long-term relationship', icon: '💕' },
    { value: 'marriage', label: 'Marriage', icon: '💍' },
    { value: 'dating', label: 'Dating', icon: '☕' },
    { value: 'friendship', label: 'Friendship', icon: '🤝' },
    { value: 'casual', label: 'Casual dating', icon: '✨' },
    { value: 'not-sure', label: 'Not sure yet', icon: '🤔' }
  ];

  const interestedIn = [
    { value: 'men', label: 'Men' },
    { value: 'women', label: 'Women' },
    { value: 'both', label: 'Men & Women' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="space-y-6 mt-6">
      <p className="text-gray-600 -mt-4">Help us match you with the right people</p>

      <div>
        <Label className="text-gray-900 font-semibold">What are you looking for?</Label>
        <div className="mt-2 space-y-2">
          {intentions.map(i => (
            <ChoiceCard
              key={i.value}
              selected={formData.looking_for === i.value}
              onClick={() => updateField('looking_for', i.value)}
              testId={`intention-${i.value}`}
            >
              <span className="mr-2">{i.icon}</span>
              {i.label}
            </ChoiceCard>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">Who are you interested in?</Label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {interestedIn.map(i => (
            <ChoiceCard
              key={i.value}
              selected={formData.interested_in === i.value}
              onClick={() => updateField('interested_in', i.value)}
              testId={`interested-in-${i.value}`}
            >
              {i.label}
            </ChoiceCard>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">Age range you're interested in</Label>
        <div className="mt-2 flex items-center gap-4 bg-white p-4 rounded-2xl border border-gray-200">
          <div className="flex-1">
            <div className="text-xs text-gray-500 mb-1">Minimum</div>
            <Input
              type="number"
              min="18"
              max="100"
              value={formData.age_min}
              onChange={(e) => updateField('age_min', e.target.value)}
              className="h-12 rounded-xl text-center font-semibold"
              data-testid="age-min-input"
            />
          </div>
          <div className="text-gray-400">—</div>
          <div className="flex-1">
            <div className="text-xs text-gray-500 mb-1">Maximum</div>
            <Input
              type="number"
              min="18"
              max="100"
              value={formData.age_max}
              onChange={(e) => updateField('age_max', e.target.value)}
              className="h-12 rounded-xl text-center font-semibold"
              data-testid="age-max-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Step3Lifestyle = ({ formData, updateField }) => {
  const questions = [
    {
      field: 'has_children',
      label: 'Do you have children?',
      icon: Baby,
      options: [
        { value: 'no', label: 'No' },
        { value: 'yes-with-me', label: 'Yes, and they live with me' },
        { value: 'yes-not-with-me', label: "Yes, but they don't live with me" },
        { value: 'prefer-not-to-say', label: 'Prefer not to say' }
      ]
    },
    {
      field: 'wants_children',
      label: 'Do you want children in the future?',
      icon: Baby,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'maybe', label: 'Maybe' },
        { value: 'not-sure', label: 'Not sure' }
      ]
    },
    {
      field: 'smoking',
      label: 'Do you smoke?',
      icon: Cigarette,
      options: [
        { value: 'no', label: 'No' },
        { value: 'occasionally', label: 'Occasionally' },
        { value: 'regularly', label: 'Regularly' },
        { value: 'prefer-not-to-say', label: 'Prefer not to say' }
      ]
    },
    {
      field: 'drinking',
      label: 'Do you drink alcohol?',
      icon: Wine,
      options: [
        { value: 'no', label: 'No' },
        { value: 'occasionally', label: 'Occasionally' },
        { value: 'socially', label: 'Socially' },
        { value: 'regularly', label: 'Regularly' },
        { value: 'prefer-not-to-say', label: 'Prefer not to say' }
      ]
    }
  ];

  return (
    <div className="space-y-8 mt-6">
      <p className="text-gray-600 -mt-4">All questions are optional</p>
      {questions.map(q => (
        <div key={q.field}>
          <div className="flex items-center gap-2 mb-2">
            <q.icon className="w-5 h-5 text-[#009E60]" />
            <Label className="text-gray-900 font-semibold">{q.label}</Label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {q.options.map(o => (
              <ChoiceCard
                key={o.value}
                selected={formData[q.field] === o.value}
                onClick={() => updateField(q.field, o.value)}
                testId={`${q.field}-${o.value}`}
              >
                {o.label}
              </ChoiceCard>
            ))}
          </div>
        </div>
      ))}

      {/* Education */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Star className="w-5 h-5 text-[#009E60]" />
          <Label className="text-gray-900 font-semibold">Highest level of education</Label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'high-school', label: 'High school' },
            { value: 'some-college', label: 'Some college' },
            { value: 'bachelor', label: "Bachelor's" },
            { value: 'master', label: "Master's" },
            { value: 'doctorate', label: 'Doctorate' },
            { value: 'trade-school', label: 'Trade school' },
            { value: 'other', label: 'Other' },
            { value: 'prefer-not-to-say', label: 'Prefer not to say' }
          ].map(o => (
            <ChoiceCard
              key={o.value}
              selected={formData.education === o.value}
              onClick={() => updateField('education', o.value)}
              testId={`education-${o.value}`}
            >
              {o.label}
            </ChoiceCard>
          ))}
        </div>
      </div>

      {/* Occupation */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-[#009E60]" />
          <Label className="text-gray-900 font-semibold">What do you do for work?</Label>
        </div>
        <Input
          value={formData.occupation}
          onChange={(e) => updateField('occupation', e.target.value.slice(0, 80))}
          placeholder="e.g., Teacher, Engineer, Nurse"
          className="h-12 rounded-xl"
          data-testid="occupation-input"
        />
      </div>
    </div>
  );
};

const Step4Interests = ({ formData, toggleArrayItem }) => {
  const interests = [
    'Cooking', 'Music', 'Dancing', 'Travel', 'Sports', 'Cricket',
    'Football', 'Basketball', 'Fitness', 'Movies', 'Beach', 'Nature',
    'Food', 'Gaming', 'Photography', 'Reading', 'Shopping', 'Cars',
    'Business', 'Family', 'Religion/Faith', 'Guyanese culture', 'Caribbean culture'
  ];

  return (
    <div className="mt-6">
      <p className="text-gray-600 -mt-4 mb-6">
        Choose what you love ({formData.interests.length}/10)
      </p>
      <div className="flex flex-wrap gap-2">
        {interests.map(interest => (
          <Chip
            key={interest}
            selected={formData.interests.includes(interest)}
            onClick={() => toggleArrayItem('interests', interest, 10)}
            testId={`interest-${interest.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
          >
            {interest}
          </Chip>
        ))}
      </div>
    </div>
  );
};

const Step5Personality = ({ formData, updateField, toggleArrayItem }) => {
  const traits = [
    'Outgoing', 'Easygoing', 'Romantic', 'Adventurous', 'Funny',
    'Ambitious', 'Family-oriented', 'Quiet', 'Social', 'A little bit of everything'
  ];
  
  const idealDates = [
    { value: 'dinner', label: 'Dinner', icon: '🍽️' },
    { value: 'coffee', label: 'Coffee', icon: '☕' },
    { value: 'beach', label: 'Beach', icon: '🏖️' },
    { value: 'movie', label: 'Movie', icon: '🎬' },
    { value: 'drinks', label: 'Drinks', icon: '🍸' },
    { value: 'dancing', label: 'Dancing', icon: '💃' },
    { value: 'cooking', label: 'Cooking together', icon: '👨‍🍳' },
    { value: 'exploring', label: 'Exploring somewhere new', icon: '🗺️' },
    { value: 'together', label: 'Just spending time together', icon: '💕' }
  ];

  const priorities = [
    'Trust', 'Communication', 'Loyalty', 'Romance', 'Family',
    'Humor', 'Financial stability', 'Shared values', 'Physical attraction', 'Companionship'
  ];

  return (
    <div className="space-y-6 mt-6">
      <p className="text-gray-600 -mt-4">Show off your personality</p>

      <div>
        <Label className="text-gray-900 font-semibold">
          How would you describe yourself? <span className="text-gray-400 font-normal text-sm">(pick any)</span>
        </Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {traits.map(t => (
            <Chip
              key={t}
              selected={formData.personality.includes(t)}
              onClick={() => toggleArrayItem('personality', t)}
              testId={`trait-${t.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            >
              {t}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">What's your ideal date?</Label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {idealDates.map(d => (
            <ChoiceCard
              key={d.value}
              selected={formData.ideal_date === d.value}
              onClick={() => updateField('ideal_date', d.value)}
              testId={`ideal-date-${d.value}`}
            >
              <span className="mr-2">{d.icon}</span>
              {d.label}
            </ChoiceCard>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">
          What matters most in a relationship? <span className="text-gray-400 font-normal text-sm">(pick up to 3)</span>
        </Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {priorities.map(p => (
            <Chip
              key={p}
              selected={formData.relationship_priorities.includes(p)}
              onClick={() => toggleArrayItem('relationship_priorities', p, 3)}
              testId={`priority-${p.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            >
              {p}
            </Chip>
          ))}
        </div>
      </div>
    </div>
  );
};

const Step6Story = ({ formData, updateField }) => {
  const promptOptions = [
    'The perfect weekend for me is…',
    'One thing you should know about me…',
    'My favorite Guyanese food is…',
    'A place I\'d love to visit is…',
    'I\'m happiest when…',
    'The quickest way to win me over is…'
  ];

  const addPrompt = (question) => {
    if (formData.prompts.length >= 3) return;
    if (formData.prompts.find(p => p.question === question)) return;
    updateField('prompts', [...formData.prompts, { question, answer: '' }]);
  };

  const updatePromptAnswer = (idx, answer) => {
    const newPrompts = [...formData.prompts];
    newPrompts[idx] = { ...newPrompts[idx], answer };
    updateField('prompts', newPrompts);
  };

  const removePrompt = (idx) => {
    updateField('prompts', formData.prompts.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-6 mt-6">
      <p className="text-gray-600 -mt-4">Give people a glimpse of who you are (optional)</p>

      <div>
        <Label className="text-gray-900 font-semibold">Tell people a little about yourself</Label>
        <Textarea
          value={formData.bio}
          onChange={(e) => updateField('bio', e.target.value.slice(0, 500))}
          placeholder="Tell us something interesting about you, what you enjoy doing, or what you're looking for."
          className="mt-2 min-h-[120px] rounded-xl resize-none"
          maxLength={500}
          data-testid="bio-input"
        />
        <p className="text-xs text-gray-400 text-right mt-1">{formData.bio.length}/500</p>
      </div>

      <div>
        <Label className="text-gray-900 font-semibold">
          Answer some prompts <span className="text-gray-400 font-normal text-sm">({formData.prompts.length}/3)</span>
        </Label>
        
        {formData.prompts.length > 0 && (
          <div className="mt-2 space-y-3">
            {formData.prompts.map((p, i) => (
              <div key={i} className="bg-white border-2 border-[#009E60]/20 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-[#009E60]">{p.question}</div>
                  <button onClick={() => removePrompt(i)} className="text-gray-400 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <Textarea
                  value={p.answer}
                  onChange={(e) => updatePromptAnswer(i, e.target.value.slice(0, 200))}
                  placeholder="Your answer..."
                  className="min-h-[70px] rounded-xl resize-none border-gray-200"
                  maxLength={200}
                  data-testid={`prompt-answer-${i}`}
                />
              </div>
            ))}
          </div>
        )}

        {formData.prompts.length < 3 && (
          <div className="mt-3 space-y-2">
            <p className="text-sm text-gray-500">Tap a prompt to add:</p>
            {promptOptions
              .filter(q => !formData.prompts.find(p => p.question === q))
              .map((q, i) => (
                <button
                  key={i}
                  onClick={() => addPrompt(q)}
                  className="w-full text-left p-3 rounded-xl border border-dashed border-gray-300 hover:border-[#009E60] hover:bg-[#009E60]/5 transition-colors"
                  data-testid={`add-prompt-${i}`}
                >
                  <span className="text-gray-700">{q}</span>
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Step7Photos = ({ formData, updateField, removePhoto, uploadingPhoto, onUploadClick }) => {
  return (
    <div className="mt-6">
      <p className="text-gray-600 -mt-4 mb-4">
        Add a main profile photo. Extra photos optional. Tap a photo to make it your main.
      </p>
      
      <div className="bg-[#FFCE00]/10 border border-[#FFCE00]/30 rounded-2xl p-3 mb-6">
        <p className="text-sm text-gray-700 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#FFCE00] flex-shrink-0" />
          Profiles with a real photo are 3x more likely to match.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[...Array(6)].map((_, i) => {
          const photo = formData.photos[i];
          const isPrimary = i === formData.primary_photo_index;
          return (
            <div key={i} className="aspect-[3/4] relative">
              {photo ? (
                <div
                  onClick={() => updateField('primary_photo_index', i)}
                  className={`w-full h-full rounded-2xl overflow-hidden relative cursor-pointer border-4 transition-colors ${
                    isPrimary ? 'border-[#009E60]' : 'border-transparent'
                  }`}
                  data-testid={`photo-${i}`}
                >
                  <img src={photo} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                  {isPrimary && (
                    <div className="absolute top-2 left-2 bg-[#009E60] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      MAIN
                    </div>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); removePhoto(i); }}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                    data-testid={`remove-photo-${i}`}
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={onUploadClick}
                  disabled={uploadingPhoto}
                  className="w-full h-full rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center hover:border-[#009E60] hover:bg-[#009E60]/5 transition-colors disabled:opacity-50"
                  data-testid={`add-photo-${i}`}
                >
                  {uploadingPhoto && i === formData.photos.length ? (
                    <Loader2 className="w-6 h-6 text-[#009E60] animate-spin" />
                  ) : (
                    <>
                      <Camera className="w-6 h-6 text-gray-400" />
                      <span className="text-xs text-gray-400 mt-1">Add</span>
                    </>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Privacy: blur toggle */}
      {formData.photos.length > 0 && (
        <div className="mt-6 bg-white rounded-2xl p-4 border border-gray-100">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#009E60]/10 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-[#009E60]" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900">Photo privacy</div>
              <div className="text-sm text-gray-500">Choose how others see your main photo</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => updateField('photo_blurred', false)}
              className={`text-left p-3 rounded-xl border-2 transition-all ${
                !formData.photo_blurred
                  ? 'border-[#009E60] bg-[#009E60]/5'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              data-testid="photo-visible"
            >
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4 text-[#009E60]" />
                <span className="font-semibold text-sm text-gray-900">Show normally</span>
              </div>
              <div className="text-xs text-gray-500">Everyone sees your photo clearly</div>
            </button>
            <button
              type="button"
              onClick={() => updateField('photo_blurred', true)}
              className={`text-left p-3 rounded-xl border-2 transition-all ${
                formData.photo_blurred
                  ? 'border-[#009E60] bg-[#009E60]/5'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              data-testid="photo-blurred"
            >
              <div className="flex items-center gap-2 mb-1">
                <EyeOff className="w-4 h-4 text-[#009E60]" />
                <span className="font-semibold text-sm text-gray-900">Blur for privacy</span>
              </div>
              <div className="text-xs text-gray-500">Members see a blurred version</div>
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            You can change this anytime in Profile Settings.
          </p>
        </div>
      )}

      {formData.photos.length > 0 && (
        <div className="mt-4 bg-white rounded-2xl p-4 border border-gray-100">
          <p className="text-sm font-semibold text-gray-900 mb-2">Preview</p>
          <p className="text-xs text-gray-500">
            You've added {formData.photos.length} photo{formData.photos.length !== 1 ? 's' : ''}.
            {formData.photos.length < 6 && ' Add more to boost your profile.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfileSetupPage;
